import { COMPLETED_FILL_PERCENT } from "@memorang/helpers/language-learning";
import { useState } from "react";
import type { LayoutChangeEvent } from "react-native";
import { Text } from "react-native-paper";
import { useAppConfig } from "../../contexts/AppConfigContext";
import { type AppTheme, useAppTheme } from "../../useAppTheme";
import { AppIcon } from "../AppIcon";
import Box from "../Box";
import DonutScore from "../DonutScore";
import { LevelIcon } from "../LevelIcon";
import {
	type ConnectedLevel,
	type ConnectedLevelSizing,
	getConnectedLevelSizing,
	withFilledPreviousLevels,
} from "./helpers";

export type { ConnectedLevel, ConnectedLevelSizing } from "./helpers";
export {
	getConnectedLevelSizing,
	toConnectedLevels,
	withFilledPreviousLevels,
} from "./helpers";

type ConnectedLevelsProps = {
	levels: ConnectedLevel[];
	size?: "sm" | "md" | "lg";
	showLabels?: boolean;
	fillPreviousLevels?: boolean;
};

const MEDIUM_BAND_THRESHOLD = 50;
const TRACK_ALPHA_SUFFIX = "4D";
const LABEL_HEIGHT = 28;
// Reserving the full label overhang as padding starves the connectors on
// narrow cards; labels may overflow this padding — they render fine beyond it.
const MAX_LABEL_OVERHANG_PADDING = 16;

const getBandColor = (progress: number, theme: AppTheme) => {
	if (progress >= COMPLETED_FILL_PERCENT) {
		return theme.palette.progress.high;
	}
	if (progress >= MEDIUM_BAND_THRESHOLD) {
		return theme.palette.progress.medium;
	}
	if (progress > 0) {
		return theme.palette.progress.low;
	}
	return theme.palette.progress.none;
};

const getTrackColor = (color: string, theme: AppTheme) =>
	color.startsWith("#") && color.length === 7
		? `${color}${TRACK_ALPHA_SUFFIX}`
		: theme.palette.progress.none;

const LevelRing = ({
	level,
	showLabel,
	sizing,
	ringSize,
}: {
	level: ConnectedLevel;
	showLabel: boolean;
	sizing: ConnectedLevelSizing;
	ringSize: number;
}) => {
	const theme = useAppTheme();
	const isComplete = level.progress >= COMPLETED_FILL_PERCENT;
	const color = getBandColor(level.progress, theme);
	const ringInnerRadius = sizing.ringRadius - sizing.ringThickness;
	const hasLabel = showLabel && Boolean(level.label);

	return (
		<Box
			width={ringSize}
			height={hasLabel ? ringSize + sizing.labelGap + LABEL_HEIGHT : undefined}
			alignItems="center"
		>
			<DonutScore
				score={level.progress}
				distribution={[level.progress, 100 - level.progress]}
				variant="progress"
				radius={sizing.ringRadius}
				innerRadius={ringInnerRadius}
				customColorScale={[color, getTrackColor(color, theme)]}
				customChildren={
					isComplete ? (
						<AppIcon
							name="check-bold"
							size={sizing.iconSize}
							color={theme.palette.progress.high}
						/>
					) : (
						<LevelIcon name={level.icon} size={sizing.iconSize} color={color} />
					)
				}
			/>
			{hasLabel ? (
				<Box
					position="absolute"
					top={ringSize + sizing.labelGap}
					width={sizing.labelWidth}
					left={(ringSize - sizing.labelWidth) / 2}
					alignItems="center"
				>
					<Text variant="titleMedium" numberOfLines={1}>
						{level.label}
					</Text>
				</Box>
			) : null}
		</Box>
	);
};

const LevelConnector = ({
	previousComplete,
	isFluid,
	sizing,
}: {
	previousComplete: boolean;
	isFluid: boolean;
	sizing: ConnectedLevelSizing;
}) => {
	const theme = useAppTheme();
	const [fluidWidth, setFluidWidth] = useState(0);
	const color = previousComplete
		? theme.palette.progress.high
		: theme.palette.progress.none;
	const stroke = sizing.connectorWidth;
	const marginTop = sizing.ringRadius - stroke / 2;
	const width = isFluid ? undefined : sizing.connectorLength;
	const availableLength = isFluid ? fluidWidth : sizing.connectorLength;
	const handleLayout = isFluid
		? (event: LayoutChangeEvent) => {
				setFluidWidth(event.nativeEvent.layout.width);
			}
		: undefined;

	if (!previousComplete) {
		const { connectorDashLength, connectorDashGap } = sizing;
		const period = connectorDashLength + connectorDashGap;
		const dashCount = Math.max(
			1,
			Math.floor((availableLength + connectorDashGap) / period),
		);

		return (
			<Box
				flex={isFluid ? 1 : undefined}
				width={width}
				height={stroke}
				marginTop={marginTop}
				flexDirection="row"
				alignItems="center"
				justifyContent="space-evenly"
				overflow="hidden"
				onLayout={handleLayout}
			>
				{Array.from({ length: dashCount }, (_, index) => `dash-${index}`).map(
					(dashKey) => (
						<Box
							key={dashKey}
							width={connectorDashLength}
							height={stroke}
							borderRadius={stroke / 2}
							backgroundColor={color}
							style={{ borderCurve: "continuous" }}
						/>
					),
				)}
			</Box>
		);
	}

	return (
		<Box
			flex={isFluid ? 1 : undefined}
			width={width}
			height={stroke}
			marginTop={marginTop}
			backgroundColor={color}
			onLayout={handleLayout}
		/>
	);
};

export const ConnectedLevels = ({
	levels,
	size = "sm",
	showLabels,
	fillPreviousLevels,
}: ConnectedLevelsProps) => {
	const appConfig = useAppConfig();

	const displayLevels =
		(fillPreviousLevels ??
		appConfig.reportView?.options?.fillPreviousProficiencyLevels)
			? withFilledPreviousLevels(levels)
			: levels;

	const shouldShowLabels = showLabels ?? size !== "sm";
	const isFluid = shouldShowLabels;
	const ringSize = size === "sm" ? 32 : size === "md" ? 48 : 58;
	const sizing = getConnectedLevelSizing(ringSize);
	const labelOverhang = shouldShowLabels
		? Math.min(
				MAX_LABEL_OVERHANG_PADDING,
				Math.max(0, (sizing.labelWidth - ringSize) / 2),
			)
		: 0;

	return (
		<Box
			flexDirection="row"
			alignItems="flex-start"
			justifyContent="center"
			width={isFluid ? "100%" : undefined}
			paddingHorizontal={labelOverhang}
		>
			{displayLevels.map((level, index) => (
				<Box
					key={level.label ?? `${level.icon}-${index}`}
					flexDirection="row"
					alignItems="flex-start"
					flex={isFluid && index > 0 ? 1 : undefined}
				>
					{index > 0 ? (
						<LevelConnector
							previousComplete={
								(displayLevels[index - 1]?.progress ?? 0) >=
								COMPLETED_FILL_PERCENT
							}
							isFluid={isFluid}
							sizing={sizing}
						/>
					) : null}
					<LevelRing
						level={level}
						showLabel={shouldShowLabels}
						sizing={sizing}
						ringSize={ringSize}
					/>
				</Box>
			))}
		</Box>
	);
};
